import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import Reducers from '../../../_shared/constants/reducers';
import {
  CardType,
  TradeContext,
  TradePaginationConfig
} from '../../../_shared/types/trade';
import PaginationButtonsLayout from '../../../pages/Layout/PaginationButtonsLayout';
import {
  changePageDecision,
  removePendingTrade
} from '../../../redux/slices/pending';
import convertToCurrency from '../../../_shared/utils/convertToCurrency';
import useTradeListPendingList from '../../../_shared/hooks/list/useTradeListPendingList';
import usePendingListTracking from '../../../_shared/hooks/list/usePendingListTracking';
import PendingListCards from '../PendingListCards';
import TradePageEmpty from '../../TradePageEmpty';
import TradeCardEmpty from '../../TradeCardEmpty';
import PendingHeader from '../PendingHeader';
import PendingListCardVariant from '../../../_shared/constants/pendingListCardVariant';
import isOmpPlatform from '../../../_shared/utils/isOmpPlatform';

const PendingDecision = ({
  activeNetwork,
  tradeContext,
  isOmp = false,
  handleNavigateToTradeUri
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [tradeIdToRemove, decisionPageNumber] = useSelector(
    state => [
      state[Reducers.PENDING_STATE]?.tradeIdToRemove,
      state[Reducers.PENDING_STATE]?.decisionPageNumber
    ],
    shallowEqual
  );
  const [state, setState] = useState({
    total: 0,
    tradeListItemsInPendingDecision: []
  });
  const { total, tradeListItemsInPendingDecision } = state;
  const pageSize = TradePaginationConfig.maxPerPage;
  const {
    data,
    hasNextPage,
    hasPreviousPage,
    isFetching,
    isFetchingPreviousPage,
    isFetchingNextPage,
    status
  } = useTradeListPendingList(
    {
      offset: decisionPageNumber * pageSize,
      size: pageSize,
      sellingNetworkId: activeNetwork,
      hasNegotiation: false
    },
    tradeContext
  );
  usePendingListTracking({ status, trades: data?.pages?.[0]?.trades });

  const isLoadingResults = () =>
    isLoading || isFetching || isFetchingPreviousPage || isFetchingNextPage;

  useEffect(() => {
    setState(prevState => {
      return {
        ...prevState,
        total: data?.pages[0].totalValue,
        tradeListItemsInPendingDecision: data?.pages[0].trades
      };
    });
    setIsLoading(false);
  }, [data?.pages, decisionPageNumber, setIsLoading, dispatch]);

  useEffect(() => {
    if (tradeListItemsInPendingDecision?.length > 0 && tradeIdToRemove) {
      const filteredTradeList = tradeListItemsInPendingDecision?.filter(
        tradeSummary => tradeSummary?.id !== tradeIdToRemove
      );
      if (
        filteredTradeList?.length !== tradeListItemsInPendingDecision?.length
      ) {
        setState(prevState => {
          return {
            ...prevState,
            tradeListItemsInPendingDecision: filteredTradeList
          };
        });
        dispatch(removePendingTrade(false));
      }
    }
  }, [dispatch, tradeIdToRemove, tradeListItemsInPendingDecision]);

  const onPageChange = () => {
    dispatch(
      changePageDecision({
        isLastPageDecision: !hasNextPage
      })
    );
  };

  const getHeading = () => {
    switch (tradeContext) {
      case TradeContext.Buy:
        return (
          <FormattedMessage
            id="list.header.buy.pending.decision.heading"
            values={{
              totalBid: convertToCurrency(total)
            }}
          />
        );
      case TradeContext.Sell:
        return (
          <FormattedMessage
            id="list.header.sell.pending.decision.heading"
            values={{
              totalBid: convertToCurrency(total)
            }}
          />
        );
      default:
        return null;
    }
  };

  const getSubHeading = () => {
    switch (tradeContext) {
      case TradeContext.Buy:
        return (
          <FormattedMessage id="list.header.buy.pending.decision.subheading" />
        );
      case TradeContext.Sell:
        return (
          <FormattedMessage id="list.header.sell.pending.decision.subheading" />
        );
      default:
        return null;
    }
  };

  const getEmptyCardHeading = () => {
    switch (tradeContext) {
      case TradeContext.Buy:
        return <FormattedMessage id="list.empty.buy.pending.decision.header" />;
      case TradeContext.Sell:
        return (
          <FormattedMessage id="list.empty.sell.pending.decision.header" />
        );
      default:
        return null;
    }
  };

  const getEmptyCardContent = () => {
    return <FormattedMessage id="list.empty.pending.decision.content" />;
  };

  const getEmptyComponent = () => {
    return isOmpPlatform() ? (
      <TradePageEmpty
        header={getEmptyCardHeading()}
        content={getEmptyCardContent()}
      />
    ) : (
      <TradeCardEmpty
        variant={CardType.PLAIN}
        header={getEmptyCardHeading()}
        content={getEmptyCardContent()}
      />
    );
  };

  return (
    <PaginationButtonsLayout
      id="pagination-pendingDecision"
      header={
        <PendingHeader
          isLoading={isLoadingResults()}
          heading={getHeading()}
          subHeading={getSubHeading()}
        />
      }
      content={
        <PendingListCards
          id="pendingDecisionCard"
          tradeContext={tradeContext}
          tradeList={tradeListItemsInPendingDecision}
          isLoading={isLoadingResults()}
          listCardVariant={PendingListCardVariant.DECISION}
          emptyCardComponent={getEmptyComponent()}
          isOmp={isOmp}
          handleNavigateToTradeUri={handleNavigateToTradeUri}
        />
      }
      pageNumber={decisionPageNumber}
      setPageNumber={setPageNumber =>
        dispatch(
          changePageDecision({
            decisionPageNumber: setPageNumber(decisionPageNumber)
          })
        )
      }
      onPageChange={onPageChange}
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
    />
  );
};
export default PendingDecision;
